import { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { LayoutDashboard, Store, Tag, Plus, ArrowLeft, CreditCard, HelpCircle, ScanLine, Star, FileText, LifeBuoy, Menu, X, Award, Globe, ExternalLink, BarChart3, Share2, Users, Mail, QrCode, Newspaper, Network, Handshake, Package, MessageCircle, ChevronDown, Megaphone, Sparkles, PlusCircle, CalendarPlus, Inbox, Settings, TrendingUp, SmartphoneNfc } from "lucide-react";

// Tableau de bord : toujours visible, hors section.
const DASHBOARD = { to: "/pro", end: true, icon: LayoutDashboard, label: "Tableau de bord", testid: "pro-nav-dashboard" };

// Menu regroupé en sections rétractables pour une navigation plus claire.
const SECTIONS = [
  {
    id: "vitrine", title: "Ma vitrine & promotions", icon: Store,
    items: [
      { to: "/pro/entreprise", icon: Store, label: "Mon mini-site", testid: "pro-nav-business",
        desc: "Mieux référencé sur Google : complétez-le !",
        tooltip: "Complétez votre mini-site local (logo, photos, présentation, horaires, contact) : c'est votre page publique sur PROXIreduc et sur Google." },
      { to: "/pro/offres", icon: Tag, label: "Mes promotions", testid: "pro-nav-offers" },
      { to: "/pro/offres/nouvelle", icon: Plus, label: "Nouvelle promo / bon plan", testid: "pro-nav-new", highlight: true,
        desc: "Attirez de nouveaux clients avec une remise.",
        tooltip: "Attirez de nouveaux clients : créez une offre promotionnelle partageable pour booster vos ventes." },
      { to: "/pro/statistiques", icon: BarChart3, label: "Statistiques", testid: "pro-nav-stats" },
      { to: "/pro/publications-facebook", icon: Megaphone, label: "Publier sur Facebook & groupes", testid: "pro-nav-fbposts" },
    ],
  },
  {
    id: "reseau", title: "Réseau Pro local", icon: Network,
    items: [
      { to: "/reseau", end: true, icon: Network, label: "Annuaire du réseau", testid: "pro-nav-network",
        tooltip: "Annuaire local (commerces, clubs, associations, organismes), mise en relation, recommandations et collaborations." },
      { to: "/reseau/connexions", icon: Handshake, label: "Mes connexions", testid: "pro-nav-network-conn" },
      { to: "/reseau/messages", icon: MessageCircle, label: "Messages", testid: "pro-nav-network-messages" },
      { to: "/reseau/annonces", icon: Package, label: "Annonces d'entraide", testid: "pro-nav-network-listings" },
      { to: "/reseau/profil", icon: Users, label: "Mon profil réseau", testid: "pro-nav-network-profile" },
    ],
  },
  {
    id: "devis", title: "Demandes de devis", icon: FileText,
    items: [
      { to: "/pro/demandes-devis", end: true, icon: Inbox, label: "Mes demandes de devis", testid: "pro-nav-quotes",        tooltip: "Recevez et répondez gratuitement aux demandes de devis des particuliers de votre secteur." },
      { to: "/pro/opportunites", icon: Sparkles, label: "Opportunités", testid: "pro-nav-opportunities",
        tooltip: "Demandes non encore satisfaites de votre métier : répondez pour décrocher de nouveaux clients." },
      { to: "/pro/demandes-devis/profil", icon: Settings, label: "Mes métiers & zones", testid: "pro-nav-quotes-profile" },
      { to: "/demande-de-devis", icon: FileText, label: "Faire une demande de devis", testid: "pro-nav-new-quote",
        tooltip: "Vous avez vous-même besoin d'un professionnel ? Faites une demande de devis gratuite." },
    ],
  },
  {
    id: "clients", title: "Mes clients (CRM)", icon: Mail,
    items: [
      { to: "/pro/clients", end: true, icon: Users, label: "Mes contacts", testid: "pro-nav-crm-contacts" },
      { to: "/pro/clients/campagnes", icon: Mail, label: "Campagnes e-mail", testid: "pro-nav-crm-campaigns" },
      { to: "/pro/clients/collecte", icon: QrCode, label: "Collecte & automatisations", testid: "pro-nav-crm-settings",
        tooltip: "Créez un QR code / formulaire pour collecter les e-mails de vos clients, puis automatisez vos relances." },
    ],
  },
  {
    id: "communaute", title: "Publier dans la communauté", icon: Megaphone,
    items: [
      { to: "/petites-annonces/deposer", icon: PlusCircle, label: "Déposer une petite annonce", testid: "pro-nav-new-annonce",
        tooltip: "Déposez une petite annonce locale (vente, don, recherche d'objet)." },
      { to: "/entre-voisins", icon: Handshake, label: "Publier une entraide / un service", testid: "pro-nav-new-voisin",
        tooltip: "Proposez ou demandez un coup de main entre voisins." },
      { to: "/proposer-un-evenement", icon: CalendarPlus, label: "Proposer un événement", testid: "pro-nav-new-event",
        tooltip: "Proposez un événement pour l'agenda local (validé par l'équipe)." },
    ],
  },
  {
    id: "animation", title: "Animation & fidélisation", icon: Share2,
    items: [
      { to: "/pro/partager-evenement", icon: Share2, label: "Partager un événement", testid: "pro-nav-event-share" },
      { to: "/pro/partager-article", icon: Newspaper, label: "Partager un article de blog", testid: "pro-nav-blog-share" },
      { to: "/pro/partager-actualite", icon: Newspaper, label: "Partager un article d'actualité", testid: "pro-nav-press-share" },
      { to: "/pro/partager-entre-voisins", icon: Handshake, label: "Partager une entraide Entre Voisins", testid: "pro-nav-voisin-share" },
      { to: "/pro/scanner", icon: ScanLine, label: "Scanner pass membre", testid: "pro-nav-scanner" },
      { to: "/pro/avis", icon: Star, label: "Avis clients", testid: "pro-nav-reviews" },
      { to: "/pro/reputation", icon: TrendingUp, label: "Ma réputation", testid: "pro-nav-reputation",
        tooltip: "Suivez votre note, vos recommandations et collectez de nouveaux avis grâce à votre QR code." },
      { to: "/pro/fidelite", icon: Award, label: "Carte de fidélité", testid: "pro-nav-loyalty" },
    ],
  },
  {
    id: "compte", title: "Mon compte", icon: CreditCard,
    items: [
      { to: "/pro/abonnement", icon: CreditCard, label: "Mon abonnement", testid: "pro-nav-subscription" },
      { to: "/pro/factures", icon: FileText, label: "Mes factures", testid: "pro-nav-invoices" },
    ],
  },
  {
    id: "aide", title: "Aide & support", icon: LifeBuoy,
    items: [
      { to: "/pro/support", icon: LifeBuoy, label: "Aide & support", testid: "pro-nav-support" },
      { to: "/pro/aide", icon: HelpCircle, label: "Centre d'aide", testid: "pro-nav-help" },
    ],
  },
];

const pathMatches = (item, pathname) =>
  item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(item.to + "/");

export default function ProLayout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [bizId, setBizId] = useState("");
  const [netPending, setNetPending] = useState(0);
  const [oppCount, setOppCount] = useState(0);
  const [open, setOpen] = useState({});
  const location = useLocation();

  useEffect(() => {
    api.get("/pro/business").then((r) => { if (r.data?.business?.id) setBizId(r.data.business.id); }).catch(() => {});
  }, []);
  useEffect(() => {
    api.get("/pro/network/dashboard").then((r) => setNetPending(r.data?.pending || 0)).catch(() => {});
    api.get("/devis/pro/opportunities/count").then((r) => setOppCount(r.data?.count || 0)).catch(() => {});
  }, [location.pathname]);

  // Ouvre automatiquement la section contenant la page active.
  useEffect(() => {
    const active = SECTIONS.find((s) => s.items.some((it) => pathMatches(it, location.pathname)));
    if (active) setOpen((o) => ({ ...o, [active.id]: true }));
  }, [location.pathname]);

  const link = ({ isActive }) => `flex items-center gap-2 pl-3 pr-2 py-2 rounded-lg text-sm ${isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`;

  const NavList = ({ onNavigate }) => (
    <nav className="space-y-1">
      <NavLink to={DASHBOARD.to} end={DASHBOARD.end} className={link} data-testid={DASHBOARD.testid} onClick={onNavigate}>
        <DASHBOARD.icon size={16} className="shrink-0" />
        <span className="font-semibold">{DASHBOARD.label}</span>
      </NavLink>

      {bizId && (
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/vitrine/${bizId}`} target="_blank" rel="noreferrer" onClick={onNavigate}
           className="flex items-center gap-2 pl-3 pr-2 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 font-semibold text-sm hover:bg-emerald-100 transition-colors mt-1"
           data-testid="pro-view-google-page">
          <Globe size={16} className="shrink-0" /> <span className="flex-1">Voir mon mini-site</span> <ExternalLink size={13} />
        </a>
      )}

      <NavLink to="/pro/assistant-ia" data-testid="pro-nav-ai" onClick={onNavigate}
        className={({ isActive }) => `flex items-center gap-2 pl-3 pr-2 py-2 rounded-lg text-sm font-semibold mt-1 ${isActive ? "bg-[#E05638] text-white" : "text-[#E05638] hover:bg-orange-50"}`}>
        <Sparkles size={16} className="shrink-0" /><span>Mon Agent IA</span>
      </NavLink>

      <NavLink to="/ma-carte" data-testid="pro-nav-carte" onClick={onNavigate}
        className={({ isActive }) => `flex items-center gap-2 pl-3 pr-2 py-2 rounded-lg text-sm font-semibold mt-1 ${isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"}`}>
        <SmartphoneNfc size={16} className="shrink-0" /><span>Ma carte de visite digitale</span>
      </NavLink>

      {SECTIONS.map((section) => {
        const isOpen = !!open[section.id];
        const hasActive = section.items.some((it) => pathMatches(it, location.pathname));
        const sectionPending = section.id === "reseau" ? netPending : (section.id === "devis" ? oppCount : 0);
        return (
          <div key={section.id} className="pt-2">
            <button
              type="button"
              onClick={() => setOpen((o) => ({ ...o, [section.id]: !o[section.id] }))}
              className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wide transition-colors ${hasActive ? "text-[#E05638]" : "text-slate-400 hover:text-slate-600 hover:bg-slate-50"}`}
              data-testid={`pro-nav-group-${section.id}`}
            >
              <section.icon size={14} className="shrink-0" />
              <span className="flex-1 text-left">{section.title}</span>
              {sectionPending > 0 && (
                <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#E05638] text-white text-[10px] font-bold grid place-items-center" data-testid="pro-nav-network-badge">{sectionPending}</span>
              )}
              <ChevronDown size={15} className={`shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>
            {isOpen && (
              <div className="mt-1 space-y-1 border-l border-slate-100 ml-4 pl-1">
                {section.items.map((item) => (
                  <NavLink key={item.to} to={item.to} end={item.end}
                           className={({ isActive }) => `flex items-start gap-2 pl-3 pr-2 py-2 rounded-lg text-sm ${isActive ? "bg-slate-900 text-white" : item.highlight ? "bg-orange-50 text-[#E05638] font-semibold hover:bg-orange-100" : "text-slate-700 hover:bg-slate-100"}`}
                           title={item.tooltip || undefined} data-testid={item.testid} onClick={onNavigate}>
                    <item.icon size={16} className="shrink-0 mt-0.5" />
                    <span className="flex-1 min-w-0">
                      <span className="block leading-tight">{item.label}</span>
                      {item.desc && <span className="block text-[11px] leading-snug mt-0.5 font-normal text-slate-400">{item.desc}</span>}
                    </span>
                    {item.to === "/reseau" && netPending > 0 && (
                      <span className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E05638] text-white text-[10px] font-bold grid place-items-center">{netPending}</span>
                    )}
                    {item.to === "/pro/opportunites" && oppCount > 0 && (
                      <span className="shrink-0 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E05638] text-white text-[10px] font-bold grid place-items-center" data-testid="pro-nav-opp-badge">{oppCount}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {bizId && (
        <a href={`${process.env.REACT_APP_BACKEND_URL}/api/vitrine/${bizId}`} target="_blank" rel="noreferrer"
           onClick={onNavigate}
           className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-100 text-slate-700 font-semibold text-sm hover:bg-slate-200 transition-colors"
           data-testid="pro-view-google-page-bottom">
          <Globe size={16}/> Ouvrir mon mini-site <ExternalLink size={13}/>
        </a>
      )}
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-50 md:flex">
      {/* Barre supérieure mobile */}
      <div className="md:hidden sticky top-0 z-30 bg-white border-b border-slate-200 flex items-center justify-between px-4 h-14">
        <div className="font-heading font-extrabold text-base">Espace Pro</div>
        <button onClick={() => setMenuOpen(true)} aria-label="Ouvrir le menu"
                className="p-2 rounded-lg hover:bg-slate-100" data-testid="pro-mobile-menu-button">
          <Menu size={22} />
        </button>
      </div>

      {/* Drawer mobile */}
      {menuOpen && (
        <div className="md:hidden fixed inset-0 z-40" data-testid="pro-mobile-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMenuOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 max-w-[82%] bg-white p-4 flex flex-col shadow-xl overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="font-heading font-extrabold text-lg">Espace Pro</div>
              <button onClick={() => setMenuOpen(false)} aria-label="Fermer le menu"
                      className="p-2 rounded-lg hover:bg-slate-100" data-testid="pro-mobile-menu-close">
                <X size={20} />
              </button>
            </div>
            <Link to="/" className="text-sm text-slate-500 inline-flex items-center gap-1 mb-4"
                  onClick={() => setMenuOpen(false)}>
              <ArrowLeft size={14} /> Retour au site
            </Link>
            <NavList onNavigate={() => setMenuOpen(false)} />
          </aside>
        </div>
      )}

      {/* Barre latérale desktop */}
      <aside className="w-64 bg-white border-r border-slate-200 p-4 hidden md:flex flex-col overflow-y-auto max-h-screen sticky top-0">
        <Link to="/" className="text-sm text-slate-500 inline-flex items-center gap-1 mb-6"><ArrowLeft size={14} /> Retour au site</Link>
        <div className="font-heading font-extrabold text-lg mb-4">Espace Pro</div>
        <NavList />
      </aside>

      <main className="flex-1 min-w-0 p-4 sm:p-6 md:p-10"><Outlet /></main>

      {/* Bouton flottant « Scanner » — accès rapide en caisse (mobile uniquement) */}
      <Link
        to="/pro/scanner"
        className="md:hidden fixed bottom-5 right-5 z-30 flex items-center gap-2 bg-[#E05638] text-white font-semibold text-sm rounded-full shadow-lg shadow-[#E05638]/30 pl-4 pr-5 py-3 active:scale-95 transition-transform"
        data-testid="pro-fab-scanner"
        aria-label="Scanner un pass"
      >
        <ScanLine size={20} /> Scanner
      </Link>
    </div>
  );
}
